import moment from "moment";
import { FC, FunctionComponent, useContext, useMemo, useState } from "react";
import { None, Option } from "space-monad";
import styled from "styled-components";
import { ReactComponent as LogoSVG } from "../../../../../assets/logo-long.svg";
import { useWindowSize } from "../../../../../components/hooks/window-size-hook";
import { CenteredFlex, columnFlex } from "../../../../../components/layout";
import { WrapWithStepperContext } from "../../../../../components/stepper/context";
import { VerificationDTO } from "../../../../../store/verification/models/dto/due-diligence";
import { VerificationService } from "../../../../../store/verification/service";
import { HeaderContainer, LaunchLogo } from "../../../common/header";
import { ConfigContext, ConfigContextProvider } from "../common/config-context";
import ExpiredForm from "../common/expired";
import {
  FormHorizontalLinearStepper,
  FormVerticalLinearStepper,
} from "../common/steppers";
import SubmittedForm from "../common/submitted";
import { AttachmentContextProvider } from "./attachment-context";
import { FormStateProvider } from "./form-context";
import { CompanyDetails } from "./pages/company-details";
import { IInitialiseFormProps, InitialiseForm } from "./pages/init";
import { KycDetailsForm } from "./pages/kyc-details";
import ResultPage from "./result-page";

const Wrapper = styled.div`
  ${columnFlex};
  flex-grow: 1;
`;

const PoweredBy = styled.a`
  ${CenteredFlex};
  justify-content: flex-end;
  font-size: 0.8em;
  margin-top: 1em;
`;

const StyledLogo = styled(LogoSVG)`
  width: 8em;
  margin-left: 1.5em;
`;

export const AdminDueDiligenceForm: FunctionComponent<{
  item: VerificationDTO.Responses.DueDiligence.Config.Get.T;
  configId: string;
}> = ({ item, configId }) => {
  const {
    header: { css },
  } = item;
  return (
    <ConfigContextProvider item={item}>
      <Wrapper>
        <HeaderContainer
          {...css?.backgroundColor}
          colour={css?.backgroundColor.end}
        >
          <LaunchLogo
            src={VerificationService.getDueDiligenceHeaderPath(configId)}
          />
        </HeaderContainer>
        <InnerComponent configId={configId} />
      </Wrapper>
      <PoweredBy
        href="http://www.sekuritance.com"
        rel="noreferrer"
        target="_blank"
      >
        <div>Powered by</div>
        <StyledLogo />
      </PoweredBy>
    </ConfigContextProvider>
  );
};

const InnerComponent: FunctionComponent<{
  configId: string;
}> = ({ configId }) => {
  const { isMobile } = useWindowSize();
  const {
    item: { dueDiligenceId, expiresAt },
    state: { status },
  } = useContext(ConfigContext);

  const [error, setError] = useState<Option<Error>>(None); // Used to display error message to user at result page

  const steps: Array<{
    title: string;
    content: FC<IInitialiseFormProps>;
  }> = useMemo(
    () => [
      ...(dueDiligenceId
        ? []
        : [
            {
              title: "Init",
              content: InitialiseForm,
            },
          ]),
      {
        title: "Company Details",
        content: CompanyDetails,
      },
      {
        title: "KYC Details",
        content: KycDetailsForm,
      },
    ],
    [InitialiseForm, KycDetailsForm, dueDiligenceId, CompanyDetails]
  );

  const hasExpired = useMemo(() => expiresAt?.isBefore(moment()), [expiresAt]);

  const stepsProps = useMemo(
    () => ({
      configId,
    }),
    [configId]
  );

  if (hasExpired) return <ExpiredForm />;
  if (status === "SUBMITTED") return <SubmittedForm />;

  return (
    <>
      <FormStateProvider>
        <AttachmentContextProvider>
          <WrapWithStepperContext>
            {isMobile ? (
              <FormVerticalLinearStepper
                stepsProps={stepsProps}
                steps={steps}
                finalPage={<ResultPage error={error} />}
              />
            ) : (
              <FormHorizontalLinearStepper
                stepsProps={stepsProps}
                steps={steps}
                finalPage={<ResultPage error={error} />}
              />
            )}
          </WrapWithStepperContext>
        </AttachmentContextProvider>
      </FormStateProvider>
    </>
  );
};
